import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const SearchFilters = ({ onFilterChange, onReset }) => {
  const [filters, setFilters] = useState({
    keyword: '',
    location: '',
    jobType: [],
    experience: '',
    salary: { min: '', max: '' },
    companySize: [],
    industry: '',
    postedWithin: ''
  });

  const jobTypeOptions = [
    { value: 'full-time', label: 'Full Time' },
    { value: 'part-time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'remote', label: 'Remote' }
  ];

  const experienceOptions = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead/Manager (10+ years)' }
  ];

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50)' },
    { value: 'small', label: 'Small (51-200)' },
    { value: 'medium', label: 'Medium (201-1000)' },
    { value: 'large', label: 'Large (1000+)' }
  ];

  const industryOptions = [
    { value: 'it', label: 'Information Technology' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'education', label: 'Education' },
    { value: 'consulting', label: 'Consulting' }
  ];

  const postedWithinOptions = [
    { value: '24h', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'all', label: 'All time' }
  ];

  const handleInputChange = (field, value) => {
    const updatedFilters = { ...filters, [field]: value };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleSalaryChange = (type, value) => {
    const updatedFilters = {
      ...filters,
      salary: { ...filters?.salary, [type]: value }
    };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleCheckboxChange = (field, value, checked) => {
    const currentValues = filters?.[field];
    const updatedValues = checked
      ? [...currentValues, value]
      : currentValues?.filter(v => v !== value);
    
    const updatedFilters = { ...filters, [field]: updatedValues };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      keyword: '',
      location: '',
      jobType: [],
      experience: '',
      salary: { min: '', max: '' },
      companySize: [],
      industry: '',
      postedWithin: ''
    };
    setFilters(resetFilters);
    onReset();
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={20} />
          Filters
        </h3>
        <Button variant="ghost" size="sm" onClick={handleReset}>
          Reset All
        </Button>
      </div>
      <div className="space-y-4">
        <Input
          label="Keywords"
          type="text"
          placeholder="Job title, skills, or company"
          value={filters?.keyword}
          onChange={(e) => handleInputChange('keyword', e?.target?.value)}
        />

        <Input
          label="Location"
          type="text"
          placeholder="City, state, or remote"
          value={filters?.location}
          onChange={(e) => handleInputChange('location', e?.target?.value)}
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Job Type
          </label>
          <div className="space-y-2">
            {jobTypeOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={filters?.jobType?.includes(option?.value)}
                onChange={(e) => handleCheckboxChange('jobType', option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        <Select
          label="Experience Level"
          options={experienceOptions}
          value={filters?.experience}
          onChange={(value) => handleInputChange('experience', value)}
          placeholder="Select experience level"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Salary Range (₹ per annum)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="Min"
              value={filters?.salary?.min}
              onChange={(e) => handleSalaryChange('min', e?.target?.value)}
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters?.salary?.max}
              onChange={(e) => handleSalaryChange('max', e?.target?.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Company Size
          </label>
          <div className="space-y-2">
            {companySizeOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={filters?.companySize?.includes(option?.value)}
                onChange={(e) => handleCheckboxChange('companySize', option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        <Select
          label="Industry"
          options={industryOptions}
          value={filters?.industry}
          onChange={(value) => handleInputChange('industry', value)}
          placeholder="Select industry"
        />

        <Select
          label="Posted Within"
          options={postedWithinOptions}
          value={filters?.postedWithin}
          onChange={(value) => handleInputChange('postedWithin', value)}
          placeholder="Select time range"
        />
      </div>
    </div>
  );
};

export default SearchFilters;